import { useState, useEffect } from "react";

interface SearchProgressProps {
  progress: number;
  stage: string;
  theme: "dark" | "light";
  startedAt?: number;
}

function useElapsed(startedAt: number | undefined) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startedAt) { setElapsed(0); return; }
    const tick = () => setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return elapsed;
}

function fmt(s: number) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export function SearchProgress({ progress, stage, theme, startedAt }: SearchProgressProps) {
  const isDark = theme === "dark";
  const elapsed = useElapsed(startedAt);

  const trackColor   = isDark ? "rgba(255,255,255,0.08)" : "rgba(30,58,138,0.08)";
  const fillGrad     = isDark
    ? "linear-gradient(to right, #FDE047, #fb923c)"
    : "linear-gradient(to right, #1E3A8A, #3b82f6)";
  const stageColor   = isDark ? "rgba(255,255,255,0.55)" : "#475569";
  const percentColor = isDark ? "rgba(255,255,255,0.3)" : "#94a3b8";
  const timerColor   = isDark ? "rgba(255,255,255,0.22)" : "#94a3b8";
  const shimmerFrom  = isDark ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.5)";

  return (
    <div className="mt-4">
      <style>{`
        @keyframes tw-shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        .tw-shimmer { animation: tw-shimmer 1.8s ease-in-out infinite; }
      `}</style>

      <div className="flex items-center justify-between mb-2 px-0.5">
        <span style={{ fontSize: "11px", color: stageColor, fontWeight: 500 }}>{stage}</span>
        <span style={{ fontSize: "11px", color: percentColor, fontFamily: "monospace" }}>{progress}%</span>
      </div>

      <div className="relative h-1.5 rounded-full overflow-hidden" style={{ background: trackColor }}>
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            width: `${progress}%`,
            background: fillGrad,
            transition: "width 0.3s ease-out",
          }}
        >
          {progress > 5 && progress < 100 && (
            <div
              className="tw-shimmer absolute inset-y-0 w-1/4 rounded-full"
              style={{ background: `linear-gradient(to right, transparent, ${shimmerFrom}, transparent)` }}
            />
          )}
        </div>
      </div>

      {startedAt != null && (
        <div className="mt-2 text-center" style={{ fontSize: "11px", color: timerColor, fontFamily: "monospace" }}>
          {fmt(elapsed)} elapsed
        </div>
      )}
    </div>
  );
}
